// ShowCustomerInfo.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { getRestaurantInfoClient } from "../actions/userActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle, faSearch } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { Modal } from "react-bootstrap";
import ReservationScreen from "../screens/ReservationScreen";
import ReservationCard from "./ReservationCard";
import { CustomModal } from "./CustomModal";
const primaryText = "#99b0ad";
const softBackground = "#FAF6E9";
const StyledUserIcon = styled(FontAwesomeIcon).attrs({
  icon: faUserCircle
})`
  font-size: 34px;
  color: ${primaryText};
  background-color: ${softBackground};
  border-radius: 50%;
  flex: 0 0 auto;
`;
/* The avatar, name and number used to be stacked and centred, which spent most
   of the modal's height on a heading. They now sit on one line so the room goes
   to the history below. */
const UserInfoSection = styled.section`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  min-width: 0;
`;
const UserText = styled.div`
  min-width: 0;
`;
const Name = styled.h3`
  font-size: 15px;
  font-weight: 650;
  margin: 0;
  color: ${primaryText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const PhoneNumber = styled.p`
  font-size: 12px;
  margin: 1px 0 0;
  color: #666;
`;
const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e9e3e1;
  width: 100%;
  margin: 10px 0 8px;
  flex: 0 0 auto;
`;
const SectionTitle = styled.h5`
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  margin: 0;
  color: ${primaryText};
`;
const ReservationsList = styled.div`
  /* Takes whatever height the modal has left over, so the cards are read whole
     and only this list scrolls — the heading and the search box stay put. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Room for the cards' own shadow, which a flush container clipped. */
  padding: 2px 4px 4px;
  margin-top: 10px;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: #a8a8a8;
  }
  -ms-overflow-style: auto;
`;

/* Search + Pagination UI (mirrors your other screen) */
const SearchContainer = styled.div`
  max-width: 420px;
  width: 100%;
  margin: 8px auto 4px;
  position: relative;
  /* Keeps its own height in the modal's flex column instead of stretching. */
  flex: 0 0 auto;
`;
const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 14px;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  font-size: 14px;
  transition: all 0.2s;
  &:focus {
    outline: none;
    border-color: #ee754c;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(238, 117, 76, 0.15);
  }
`;
const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #aaa;
`;
/* The result count used to be a large orange pill on a row of its own. With a
   single page of results both page buttons were replaced by hidden
   placeholders, so the pill sat there as a ball floating between the search box
   and the list. The count is now a small badge on the section heading, styled
   like the notification bell's counter, and the page controls only appear when
   there is somewhere to page to. */
const TitleRow = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  flex: 0 0 auto;
`;

const CountBadge = styled.span`
  position: absolute;
  top: -7px;
  right: -22px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 999px;
  background: #ee754c;
  color: white;
  font-size: 10px;
  font-weight: 800;
  line-height: 17px;
  text-align: center;
  box-sizing: border-box;
  pointer-events: none;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 4px 0 0;
  flex: 0 0 auto;
`;

const PageButton = styled.button`
  padding: 4px 12px;
  border: 1px solid #e9e3e1;
  border-radius: 999px;
  background: #faf8f6;
  color: #61585a;
  font-size: 0.74rem;
  font-weight: 750;
  white-space: nowrap;
  cursor: pointer;
  transition: 0.14s ease;

  &:hover:not(:disabled) {
    border-color: #ee754c;
    color: #cf5b36;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const PageStatus = styled.span`
  color: #8d8586;
  font-size: 0.72rem;
  font-weight: 750;
  font-variant-numeric: tabular-nums;
`;
function ShowCustomerInfo({
  phone_number,
  restaurant_id,
  isModalActive,
  setIsModalActive
}) {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [tokens, setTokens] = useState("");
  const [reset, setReset] = useState(false);

  // NEW: search + pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const clientInfo = useSelector(state => state.restaurant_clients);
  const {
    restaurantInfoClients,
    isLoadingGetRestaurantInfoClient,
    errorGetRestaurantInfoClient
  } = clientInfo;

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // reset to page 1 on new search
    }, 600);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset search + page when a new client opens
  useEffect(() => {
    if (isModalActive) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setCurrentPage(1);
    }
  }, [isModalActive, phone_number]);

  // Fetch data whenever dependencies change
  useEffect(() => {
    if (phone_number && restaurant_id) {
      dispatch(getRestaurantInfoClient(phone_number, restaurant_id, currentPage, debouncedSearchTerm));
    }
  }, [dispatch, phone_number, restaurant_id, currentPage, debouncedSearchTerm]);
  const closeModal = () => setIsModalActive(false);
  const handleCloseReservationModal = () => {
    setIsReservationModalOpen(false);
  };
  const handleOpenEditReservationModal = tokeni => {
    setIsReservationModalOpen(true);
    setIsCreatingReservation(false);
    setTokens(tokeni);
  };

  // Read paginated data safely (supports new + old API shape)
  const totalResults = restaurantInfoClients?.reservations?.total_results ?? (Array.isArray(restaurantInfoClients?.reservations) ? restaurantInfoClients.reservations.length : 0);
  const PAGE_SIZE = 10;
  const totalPages = restaurantInfoClients?.reservations?.total_pages ?? (Math.ceil((totalResults || 0) / PAGE_SIZE) || 0); // grouped right side

  const pageResults = Array.isArray(restaurantInfoClients?.reservations) ? restaurantInfoClients.reservations : restaurantInfoClients?.reservations?.results ?? [];
  const memoizedReservations = useMemo(() => pageResults, [pageResults]);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(p => p - 1);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(p => p + 1);
  return <>
      <CustomModal show={isModalActive} onHide={closeModal} fullHeight title={t("ui.components.showCustomerInfo.cliente")}>
        <UserInfoSection>
          <StyledUserIcon />

          <UserText>
            {isLoadingGetRestaurantInfoClient && <PhoneNumber>{t("ui.components.showCustomerInfo.carregando")}</PhoneNumber>}

            {restaurantInfoClients && <>
                <Name>{restaurantInfoClients.clientInfo.client.name}</Name>
                {restaurantInfoClients.clientInfo.client.phone_number.length > 4 ? <PhoneNumber>
                    +
                    {restaurantInfoClients.clientInfo.client.phone_number.slice(0, 3)}{" "}
                    {restaurantInfoClients.clientInfo.client.phone_number.slice(3)}
                  </PhoneNumber> : <PhoneNumber>{t("ui.components.showCustomerInfo.semNumero")}</PhoneNumber>}
              </>}
          </UserText>
        </UserInfoSection>

        <Divider />
        <TitleRow>
          <SectionTitle>{t("ui.components.showCustomerInfo.historicoDeReservas")}</SectionTitle>
          {totalResults > 0 && <CountBadge>{totalResults > 99 ? "99+" : totalResults}</CountBadge>}
        </TitleRow>

        {/* Search like the first screen */}
        <SearchContainer>
          <SearchInput type="text" placeholder={t("ui.components.showCustomerInfo.procurarNasReservasPorNome")} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          <SearchIcon icon={faSearch} />
        </SearchContainer>

        {/* Page controls only when there is somewhere to page to. */}
        {totalPages > 1 && <Pager>
            <PageButton type="button" onClick={handlePrevPage} disabled={currentPage <= 1}>
              {t("ui.components.showCustomerInfo.anterior")}
            </PageButton>
            <PageStatus>
              {currentPage} / {totalPages}
            </PageStatus>
            <PageButton type="button" onClick={handleNextPage} disabled={currentPage >= totalPages}>
              {t("ui.components.showCustomerInfo.proxima")}
            </PageButton>
          </Pager>}

        <ReservationsList>
          {restaurantInfoClients && <ReservationCard reservations={memoizedReservations} isShowCustomerInfo={true} handleEdit={handleOpenEditReservationModal} />}
          {!isLoadingGetRestaurantInfoClient && memoizedReservations.length === 0 && <p className="text-center mt-3">{t("ui.components.showCustomerInfo.nenhumaReservaEncontrada")}</p>}
        </ReservationsList>
      </CustomModal>

      <Modal show={isReservationModalOpen} onHide={handleCloseReservationModal} centered size="lg">
        <ReservationScreen from_restaurant={true} creating={isCreatingReservation} setCreating={setIsCreatingReservation} isEditModalOpen={isReservationModalOpen} setIsEditModalOpen={setIsReservationModalOpen} restaurantId={restaurant_id} handleCloseReservationModal={handleCloseReservationModal} token_from_restaurant={tokens} reset={reset} />
      </Modal>
    </>;
}
export default ShowCustomerInfo;
